import {
  MIN_ANSWER_LENGTH,
  buildRevealSchedule,
  getRevealBucket,
  maxAutoRevealCount,
  normalizeAnswer,
} from "@lexireveal/game-engine";

/**
 * Estimation mécanique de la difficulté (CNT-008).
 *
 * **Assiste le relecteur, ne décide jamais à sa place** (guide §5). Le score
 * n'observe que la structure du mot et ce que le planning en donne : il ne sait
 * rien de la fréquence d'usage ni de la précision de la définition, qui sont les
 * deux premiers critères du guide. Une entrée dont la note automatique et la
 * note humaine divergent est intéressante, pas fautive.
 */

/** Lettres rares en français : leur présence contraint fortement la recherche. */
const RARE_LETTERS = new Set(["K", "W", "X", "Y", "Z", "J", "Q"]);

/** Au-delà de cette part de positions distinctes, le mot offre peu de structure. */
const HIGH_DISTINCT_RATIO = 0.9;
/** À partir de cette longueur de suite révélée, un fragment devient lisible. */
const READABLE_RUN = 3;
/** Part révélée à partir de laquelle le jeu donne beaucoup. */
const GENEROUS_REVEAL_RATIO = 0.6;
/** Nombre de concurrents de même longueur au-delà duquel le champ est encombré. */
const CROWDED_LENGTH = 15;

export type DifficultySignals = {
  readonly normalizedLength: number;
  readonly bucket: string;
  readonly distinctLetters: number;
  readonly distinctRatio: number;
  readonly repeatedLetters: readonly string[];
  readonly rareLetters: readonly string[];
  readonly revealBudget: number;
  readonly hiddenAtEnd: number;
  readonly revealedRatioAtEnd: number;
  /** Plus longue suite de positions adjacentes révélées : un fragment lisible. */
  readonly longestRevealedRun: number;
  readonly scoreWindowMs: number;
  readonly sameLengthCandidates: number;
};

export type DifficultyEstimate = {
  readonly score: 1 | 2 | 3 | 4 | 5;
  readonly signals: DifficultySignals;
  /** Ce qui a fait monter ou descendre la note, en clair. */
  readonly reasons: readonly string[];
};

function longestRun(positions: readonly number[]): number {
  const sorted = [...positions].sort((a, b) => a - b);
  let best = sorted.length === 0 ? 0 : 1;
  let current = best;

  for (let index = 1; index < sorted.length; index += 1) {
    const previous = sorted[index - 1];
    const value = sorted[index];
    if (previous === undefined || value === undefined) continue;
    current = value === previous + 1 ? current + 1 : 1;
    best = Math.max(best, current);
  }

  return best;
}

export function estimateDifficulty(
  displayWord: string,
  options: { readonly sameLengthCandidates?: number; readonly seed?: string } = {},
): DifficultyEstimate {
  const normalized = normalizeAnswer(displayWord);
  if (normalized.length < MIN_ANSWER_LENGTH) {
    throw new Error(`« ${displayWord} » : ${normalized.length} lettres, minimum ${MIN_ANSWER_LENGTH}`);
  }

  const bucket = getRevealBucket(normalized.length);
  const schedule = buildRevealSchedule({
    normalizedLength: normalized.length,
    seed: options.seed ?? displayWord,
  });

  const counts = new Map<string, number>();
  for (const char of normalized) counts.set(char, (counts.get(char) ?? 0) + 1);

  const repeated = [...counts.entries()].filter(([, n]) => n > 1).map(([char]) => char);
  const rare = [...counts.keys()].filter((char) => RARE_LETTERS.has(char));
  const budget = maxAutoRevealCount(normalized.length);

  const signals: DifficultySignals = {
    normalizedLength: normalized.length,
    bucket: Number.isFinite(bucket.maxLength)
      ? `${bucket.minLength}–${bucket.maxLength}`
      : `${bucket.minLength}+`,
    distinctLetters: counts.size,
    distinctRatio: counts.size / normalized.length,
    repeatedLetters: repeated,
    rareLetters: rare,
    revealBudget: budget,
    hiddenAtEnd: normalized.length - budget,
    revealedRatioAtEnd: budget / normalized.length,
    longestRevealedRun: longestRun(schedule.order),
    scoreWindowMs: bucket.scoreWindowMs,
    sameLengthCandidates: options.sameLengthCandidates ?? 0,
  };

  const reasons: string[] = [];
  let score = 3;

  if (signals.rareLetters.length > 0) {
    score += 1;
    reasons.push(`lettre rare (${signals.rareLetters.join(", ")}) : contraint la recherche`);
  }
  if (signals.distinctRatio >= HIGH_DISTINCT_RATIO) {
    score += 1;
    reasons.push("presque aucune lettre répétée : peu de structure à exploiter");
  }
  if (signals.repeatedLetters.length >= 2) {
    score -= 1;
    reasons.push(`plusieurs lettres répétées (${signals.repeatedLetters.join(", ")}) : une révélation en donne deux`);
  }
  if (signals.longestRevealedRun >= READABLE_RUN) {
    score -= 1;
    reasons.push(`${signals.longestRevealedRun} positions révélées d'affilée : un fragment devient lisible`);
  }
  if (signals.longestRevealedRun <= 1 && budget >= 3) {
    score += 1;
    reasons.push("révélations dispersées : aucun fragment lisible");
  }
  if (signals.revealedRatioAtEnd >= GENEROUS_REVEAL_RATIO) {
    score -= 1;
    reasons.push(
      `${Math.round(signals.revealedRatioAtEnd * 100)} % des positions finissent révélées : le jeu donne beaucoup`,
    );
  }
  if (signals.normalizedLength >= 12) {
    score += 1;
    reasons.push("mot long : plus coûteux à tenir en tête malgré un budget plus large");
  }
  if (signals.sameLengthCandidates >= CROWDED_LENGTH) {
    score += 1;
    reasons.push(`${signals.sameLengthCandidates} candidats de même longueur : champ encombré`);
  }

  const clamped = Math.min(5, Math.max(1, score)) as 1 | 2 | 3 | 4 | 5;
  return { score: clamped, signals, reasons };
}
